import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import {
	BehaviorSubject,
	firstValueFrom,
	Observable,
	of,
	takeUntil,
	tap,
	timer,
} from 'rxjs';

import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZES } from '../../../core/dx-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	initSemanticRuleList,
	openDeleteConfirmationPopup,
	refreshSemanticRuleList,
	SemanticRuleSelector,
} from '../../store';
import { SemanticRuleItemEntity } from './semantic-rule-item.entity';
import { SemanticRuleListEntity } from './semantic-rule-list.entity';

@Component({
	selector: 'app-semantic-rule-list',
	templateUrl: './semantic-rule-list.component.html',
	styleUrls: ['./semantic-rule-list.component.scss'],
})
export class SemanticRuleListComponent
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public ActionIcons = ActionIcons;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: SemanticRuleListEntity;
	}>;

	public constructor(
		private readonly store: Store,
		private readonly semanticRuleSelector: SemanticRuleSelector,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'semantic-rule-list';
		this.dxLocalStorageKey = 'dx.grid.semantic-rule-list';

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.semanticRuleSelector.getLoading$(),
			list: this.semanticRuleSelector.getSemanticRuleList$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initSemanticRuleList());
		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshSemanticRuleList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}
	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public createNewSemanticRule(): void {}

	public duplicateSemanticRule(rule: SemanticRuleItemEntity): void {}

	public deleteSemanticRule(rule: SemanticRuleItemEntity): void {
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				semanticRuleId: [rule.id],
				semanticRuleLabel: rule.label,
			})
		);
	}
	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedRules = await firstValueFrom(
			this.semanticRuleSelector.getSelectedSemanticRules$(selectedRowKeys)
		);
		if (selectedRules.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					semanticRuleId: [selectedRules[0].id],
					semanticRuleLabel: selectedRules[0].label,
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				semanticRuleId: selectedRules.map((k) => k.id),
				semanticRuleLabel: '',
			})
		);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [ActionIcons.Delete];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 1,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@semantic-rule.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
